import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { trpcClient } from '@/utils/trpc';
import { getInfinexError } from '@infinex-private/connect-utils';
import { ToastQueue } from '@infinex/ui/components';

const USER_QUERY_KEY = ['current-user'] as const;

export function useUser() {
  const queryClient = useQueryClient();

  const userQuery = useQuery({
    queryKey: USER_QUERY_KEY,
    queryFn: () => trpcClient.app.session.getCurrentUser.query(),
    retry: (_count, error) => {
      const infinexError = getInfinexError(error);
      return infinexError?.type !== 'NEEDS_CREDENTIALS';
    },
  });

  const userIsLoading = userQuery.isLoading;
  const userData = userQuery.error ? null : (userQuery.data ?? null);
  const user = userData?.user ?? null;
  const globalSessionId = userData?.globalSessionId ?? null;

  useEffect(() => {
    if (userQuery.error) {
      const infinexError = getInfinexError(userQuery.error);
      if (infinexError?.type !== 'NEEDS_CREDENTIALS') {
        console.error('Error querying user: ', userQuery.error);
        ToastQueue.add('error', {
          title: 'Something went wrong…',
          content: 'Please try again later.',
        });
      }
    }
  }, [userQuery.error]);

  // Throw if no user found after loading
  useEffect(() => {
    if (userIsLoading) return;
    if (!user) {
      throw new Error('No user found - user must be authenticated');
    }
  }, [userIsLoading, user]);

  const invalidateUser = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
  }, [queryClient]);

  const clearUser = useCallback(() => {
    return queryClient.removeQueries({ queryKey: USER_QUERY_KEY });
  }, [queryClient]);

  return {
    isLoading: userQuery.isLoading,
    user,
    globalSessionId,
    invalidateUser,
    clearUser,
  };
}
